import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase/config';

const SESSION_KEY = 'me_app_user';

const AuthContext = createContext(null);

function sanitizeUserProfile(nextUser) {
  if (!nextUser || typeof nextUser !== 'object') return null;
  const { pin: PIN, ...safeUser } = nextUser;
  void PIN;
  return safeUser;
}

function readStoredUser() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY) || localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    return sanitizeUserProfile(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeStoredUser(nextUser) {
  const safeUser = sanitizeUserProfile(nextUser);

  if (!safeUser) {
    sessionStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(SESSION_KEY);
    return null;
  }

  const raw = JSON.stringify(safeUser);
  sessionStorage.setItem(SESSION_KEY, raw);
  localStorage.setItem(SESSION_KEY, raw);
  return safeUser;
}

function buildSessionUser(firebaseUser, profile) {
  const safeProfile = sanitizeUserProfile(profile);

  return {
    ...safeProfile,
    id: safeProfile?.id ?? firebaseUser.uid,
    name: safeProfile?.name ?? firebaseUser.displayName ?? firebaseUser.email ?? 'User',
    role: safeProfile?.role ?? 'staff',
    avatar: safeProfile?.avatar ?? '👤',
    firebaseUid: firebaseUser.uid,
  };
}

export function AuthProvider({ children }) {
  const stagedUserRef = useRef(readStoredUser());
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (!firebaseUser) {
        stagedUserRef.current = null;
        writeStoredUser(null);
        setUser(null);
        setIsLoading(false);
        return;
      }

      const stagedUser = stagedUserRef.current ?? readStoredUser();
      const nextUser = buildSessionUser(firebaseUser, stagedUser);

      stagedUserRef.current = writeStoredUser(nextUser);
      setUser(nextUser);
      setIsLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = useCallback((nextUser) => {
    const safeUser = writeStoredUser(nextUser);
    stagedUserRef.current = safeUser;

    if (auth.currentUser && safeUser) {
      setUser(buildSessionUser(auth.currentUser, safeUser));
    }
  }, []);

  const logout = useCallback(async () => {
    setIsLoading(true);
    stagedUserRef.current = null;
    writeStoredUser(null);

    try {
      await signOut(auth);
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      if (!prev) return prev;
      const merged = { ...prev, ...updates };
      writeStoredUser(merged);
      stagedUserRef.current = sanitizeUserProfile(merged);
      return merged;
    });
  }, []);

  const value = useMemo(
    () => ({
      user,
      isLoading,
      login,
      logout,
      updateUser,
      // Backward-compatible aliases
      handleLogin: login,
      handleLogout: logout,
    }),
    [user, isLoading, login, logout, updateUser]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
